import { Button, Card, Flex, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@aws-amplify/ui-react";
import type { Edulead } from 'edulead';
import { useContext, useEffect, useState } from "react";
import { FaExternalLinkAlt } from 'react-icons/fa';
import ConfirmModal from "../custom-components/ConfirmModal";
import { CopyToClipboard } from "../custom-components/CopyToClipboard";
import LoadingBars from "../custom-components/LoadingBars";
import NA from "../custom-components/NA";
import { RemoveButton } from "../custom-components/RemoveButton";
import { GlobalContext, GlobalContextProps } from "../global-context";
import { allPages, toastV4Error, v4Error } from "../services/edulead-v4";
import { eduleadV4 } from "../services/edulead-v4-client";
import General from "../utils/GeneralUtils";

export function ApiPage(props: any) {

  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'API', globalContext);

  const [apiKeys, setApiKeys] = useState<Edulead.EduleadV4ApiKey[]>();
  /** False when the caller lacks api-keys:manage (by role or grant): the API says so, not the page. */
  const [canManage, setCanManage] = useState(true);
  const [createRequestId, setCreateRequestId] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  /** The key just created, with its secret, shown once. */
  const [createdKey, setCreatedKey] = useState<Edulead.EduleadV4ApiKey | null>(null);
  /** The last secret created on this page, used in the example until the page is left. */
  const [keyToDelete, setKeyToDelete] = useState<Edulead.EduleadV4ApiKey | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  async function loadApiKeys() {
    if (!globalContext?.authInfo?.teamId) return;
    try {
      setApiKeys(await allPages(eduleadV4.apiKeys.list({ page_size: 100 })));
      setCanManage(true);
    } catch (error) {
      if (v4Error(error).reason === 'PERMISSION_REQUIRED') {
        setCanManage(false);
        setApiKeys([]);
      } else {
        toastV4Error(error, 'Failed to load API keys');
      }
    }
  }

  useEffect(() => {
    loadApiKeys()
  }, [globalContext?.authInfo?.teamId])

  function openCreate() {
    setNewKeyName('');
    // One request id per user action, so a retried click cannot mint a second key
    setCreateRequestId(crypto.randomUUID());
  }

  async function confirmCreate() {
    if (!createRequestId) return;
    setIsCreating(true);
    try {
      const res = await eduleadV4.apiKeys.create({ request_id: createRequestId, body: { name: newKeyName.trim() } });
      const key = res.api_key;
      if (key) {
        setApiKeys((prev) => [{ ...key, secret: undefined }, ...(prev ?? []).filter((k) => k.id !== key.id)]);
        setCreatedKey(key);
      }
      setCreateRequestId(null);
    } catch (error) {
      toastV4Error(error, 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  }

  async function confirmDelete() {
    if (!keyToDelete?.id) return;
    const id = keyToDelete.id;
    setIsDeleting(true);
    try {
      await eduleadV4.apiKeys.delete({ id });
      setApiKeys((prev) => prev?.filter((k) => k.id !== id));
      setKeyToDelete(null);
    } catch (error) {
      toastV4Error(error, 'Failed to delete API key');
    } finally {
      setIsDeleting(false);
    }
  }

  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div className="content content-1000">
          <Flex justifyContent='space-between' alignItems='center'>
            <h4 style={{ margin: 0 }}>API Keys</h4>
            {canManage && apiKeys && (
              <Button className='tiny' onClick={openCreate}>Create API key</Button>
            )}
          </Flex>
          <div style={{ marginTop: 10 }}>
            {
              !apiKeys
                ? <LoadingBars numberOfRows={2} />
                : !canManage
                  ? <div className='desc-2'>You don't have permission to manage API keys. Ask a team owner for a key.</div>
                  : apiKeys.length === 0
                    ? <NA text='No API keys yet' />
                    : <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell as="th">Name</TableCell>
                          <TableCell as="th">Key</TableCell>
                          <TableCell as="th">Created</TableCell>
                          <TableCell as="th"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apiKeys.map((key) => (
                          <TableRow key={key.id}>
                            <TableCell>{key.name || <span className='desc-2'>No name</span>}</TableCell>
                            <TableCell><span className="code-11">{key.prefix}…</span></TableCell>
                            <TableCell>{key.create_time ? General.formatDate(key.create_time) : '—'}</TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                              <RemoveButton
                                title='Delete API key'
                                onClick={() => setKeyToDelete(key)}
                                isDisabled={isDeleting}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
            }
          </div>
          <h4>Documentation</h4>
          <Card variation='outlined' style={{ marginTop: 10, padding: 20 }}>
            <div style={{ marginBottom: 15 }}>
              Read our API documentation on <a href="https://docs.getedulead.com" target="_blank" rel="noopener noreferrer">docs.getedulead.com</a>
            </div>
            <Button
              variation='primary'
              size='small'
              onClick={event => { window.open('https://docs.getedulead.com', '_blank') }}
            >
              Open Documentation <FaExternalLinkAlt size={11} style={{ marginLeft: 5 }} />
            </Button>
          </Card>
        </div>

        <ConfirmModal
          isOpen={createRequestId !== null}
          onClose={() => setCreateRequestId(null)}
          onConfirm={confirmCreate}
          title='Create API key'
          message={
            <TextField
              label='Name (optional)'
              placeholder='e.g. CRM sync'
              maxLength={100}
              value={newKeyName}
              onChange={(event) => setNewKeyName(event.target.value)}
            />
          }
          confirmText='Create'
          isLoading={isCreating}
          loadingText='Creating...'
        />

        <ConfirmModal
          isOpen={createdKey !== null}
          onClose={() => setCreatedKey(null)}
          onConfirm={() => setCreatedKey(null)}
          title='Your new API key'
          message={
            <Flex direction='column' gap={10}>
              <Flex alignItems='center' gap={6}>
                <span className="code-11" style={{ wordBreak: 'break-all' }}>{createdKey?.secret}</span>
                {createdKey?.secret && <CopyToClipboard value={createdKey.secret} title='Copy API key' />}
              </Flex>
              <div className='desc-2'>Copy it now: you won't see this key again.</div>
            </Flex>
          }
          confirmText='Done'
          hideCancel
        />

        <ConfirmModal
          isOpen={keyToDelete !== null}
          onClose={() => setKeyToDelete(null)}
          onConfirm={confirmDelete}
          title='Delete API key?'
          message={<>Requests with <span className="code-11">{keyToDelete?.prefix}…</span>{keyToDelete?.name ? ` (${keyToDelete.name})` : ''} stop working.</>}
          confirmText='Delete'
          variant='destructive'
          isLoading={isDeleting}
          loadingText='Deleting...'
        />
      </>
    }
  </GlobalContext.Consumer>
}
